import type { Feature, FeatureCollection } from 'geojson'

import { ArchiveError } from './errors'
import { resolveImage, type ImageSlot, type PhotoRef } from './imageIndex'

export type DocPoint = {
  lon: number
  lat: number
  props: () => Record<string, unknown>
  imagePath: string
  time: number | null
  track: string
  image: PhotoRef | null
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}

export const parseDocumentationGeojson = (text: string): Feature[] => {
  let data: unknown
  try {
    data = JSON.parse(text) as unknown
  } catch {
    throw new ArchiveError('bad-geojson')
  }
  if (!data || typeof data !== 'object' || !Array.isArray((data as FeatureCollection).features)) {
    throw new ArchiveError('bad-geojson')
  }
  return (data as FeatureCollection).features
}

const EPOCH_SECONDS_LIMIT = 1e11
const NUMERIC_RE = /^\s*-?\d+(\.\d+)?\s*$/

export const parseTime = (value: unknown): number | null => {
  if (typeof value === 'number' || (typeof value === 'string' && NUMERIC_RE.test(value))) {
    const number = Number(value)
    if (!Number.isFinite(number)) return null
    return Math.abs(number) < EPOCH_SECONDS_LIMIT ? number * 1000 : number
  }
  if (typeof value !== 'string') return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

const trackOrder = new Intl.Collator(undefined, { numeric: true })

export const comparePoints = (a: DocPoint, b: DocPoint): number => {
  if (a.track !== b.track) return trackOrder.compare(a.track, b.track)
  if (a.time == null || b.time == null) return a.time == null ? (b.time == null ? 0 : 1) : -1
  return a.time - b.time
}

export const buildFeatures = (source: Feature[], index: Map<string, ImageSlot>): DocPoint[] => {
  const rows: DocPoint[] = []
  const seen = new Set<string>()
  source.forEach(feature => {
    const coords = feature?.geometry && feature.geometry.type === 'Point' ? feature.geometry.coordinates : null
    if (!coords || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') return
    const props = asRecord(feature.properties)
    const imagePath = String(props.image_path || props.img_path || '')
    const key = imagePath || `${String(props.gpstime)}@${coords[0]},${coords[1]}`
    if (seen.has(key)) return
    seen.add(key)
    rows.push({
      lon: coords[0],
      lat: coords[1],
      props: () => props,
      imagePath,
      time: parseTime(props.gpstime),
      track: props.idtrack == null ? '' : String(props.idtrack),
      image: resolveImage(index, imagePath),
    })
  })
  rows.sort(comparePoints)
  return rows
}

export type GapKind = 'exact' | 'seconds' | 'minutes' | 'hours'

export const gapParts = (milliseconds: number): { kind: GapKind; value: number } => {
  const seconds = Math.round(Math.abs(milliseconds) / 1000)
  if (seconds < 1) return { kind: 'exact', value: 0 }
  if (seconds < 60) return { kind: 'seconds', value: seconds }
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return { kind: 'minutes', value: minutes }
  return { kind: 'hours', value: Math.floor(minutes / 60) }
}

export const parseTimeQuery = (text: string, reference: Date): number | null => {
  const query = text.trim()
  if (!query) return null
  const match = query.match(/^(\d{1,2})[:.h ]?(\d{2})(?:[:.]?(\d{2}))?$/)
  if (match) {
    const hours = Number(match[1])
    const minutes = Number(match[2])
    const seconds = Number(match[3] || 0)
    if (hours > 23 || minutes > 59 || seconds > 59) return null
    const stamp = new Date(reference)
    stamp.setHours(hours, minutes, seconds, 0)
    return stamp.getTime()
  }
  const parsed = Date.parse(query)
  return Number.isNaN(parsed) ? null : parsed
}

export const nearestTime = (times: Array<number | null>, target: number): { index: number; gap: number } | null => {
  let best = -1
  let bestGap = Infinity
  times.forEach((time, index) => {
    if (time == null) return
    const gap = Math.abs(time - target)
    if (gap < bestGap) {
      bestGap = gap
      best = index
    }
  })
  if (best < 0) return null
  return { index: best, gap: bestGap }
}
